import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sparkles, User, LogOut, Menu, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Navigation() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    checkAuthStatus();
  }, []);
  
  const checkAuthStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      // User not logged in
      setUser(null);
    }
  };
  
  const isActive = (path) => location.pathname === path;
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };
  
  return (
    <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link 
            to="/" 
            className="flex items-center gap-3"
            data-testid="nav-logo"
          >
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-sm">
              <Sparkles className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-xl font-semibold text-slate-900">Learn with Zeenat</span>
          </Link>
          
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              data-testid="nav-home"
              className={`text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl px-4 py-2 transition-colors font-medium ${
                isActive('/') ? 'text-blue-600 bg-blue-50' : ''
              }`}
            >
              Home
            </Link>
            <Link
              to="/ai-tools"
              data-testid="nav-ai-tools"
              className={`text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl px-4 py-2 transition-colors font-medium ${
                isActive('/ai-tools') ? 'text-blue-600 bg-blue-50' : ''
              }`}
            >
              AI Tools
            </Link>
            <Link
              to="/resources"
              data-testid="nav-resources"
              className={`text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl px-4 py-2 transition-colors font-medium ${
                isActive('/resources') || isActive('/browse') ? 'text-blue-600 bg-blue-50' : ''
              }`}
            >
              Resources
            </Link>
            <Link
              to="/parent"
              data-testid="nav-parent-portal"
              className={`text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl px-4 py-2 transition-colors font-medium ${
                location.pathname.startsWith('/parent') ? 'text-blue-600 bg-blue-50' : ''
              }`}
            >
              Parent Portal
            </Link>
            <Link
              to="/teacher-tips"
              data-testid="nav-teacher-tips"
              className={`text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl px-4 py-2 transition-colors font-medium ${
                isActive('/teacher-tips') ? 'text-blue-600 bg-blue-50' : ''
              }`}
            >
              Teacher Tips
            </Link>
            <Link
              to="/pricing"
              data-testid="nav-pricing"
              className={`text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl px-4 py-2 transition-colors font-medium ${
                isActive('/pricing') ? 'text-blue-600 bg-blue-50' : ''
              }`}
            >
              Membership
            </Link>
          </div>
          
          {/* Mobile Hamburger Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-slate-600 hover:text-blue-600 p-2"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" strokeWidth={2} />
            ) : (
              <Menu className="w-6 h-6" strokeWidth={2} />
            )}
          </button>
          
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl px-3 py-2 transition-colors font-medium"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border-2 border-blue-100" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                      <span className="text-white text-sm font-bold">{user.name?.charAt(0)}</span>
                    </div>
                  )}
                  <span className="hidden md:inline">{user.name?.split(' ')[0]}</span>
                </button>
                
                {showUserMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowUserMenu(false)}
                    ></div>
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-100 py-2 z-20">
                      <Link
                        to="/account"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-2 text-slate-700 hover:bg-blue-50 transition-colors"
                      >
                        <User className="w-4 h-4" />
                        My Account
                      </Link>
                      {user.is_admin && (
                        <Link
                          to="/admin"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-3 px-4 py-2 text-purple-600 hover:bg-purple-50 transition-colors font-medium"
                        >
                          <Sparkles className="w-4 h-4" />
                          Admin Dashboard
                        </Link>
                      )}
                      <div className="border-t border-slate-100 my-2"></div>
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-4 py-2 text-slate-700 hover:bg-slate-50 transition-colors w-full text-left"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl px-4 py-2 transition-colors font-medium"
                >
                  Sign In
                </Link>
                <Link
                  to="/pricing"
                  className="bg-blue-600 text-white hover:bg-blue-700 rounded-xl px-6 py-2 transition-all shadow-sm hover:shadow-md active:scale-95 font-medium"
                >
                  Try Free
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-20 left-0 right-0 bg-white border-t border-slate-100 shadow-lg">
          <div className="px-6 py-4 space-y-2">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`block text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl px-4 py-3 transition-colors font-medium ${
                isActive('/') ? 'text-blue-600 bg-blue-50' : ''
              }`}
            >
              Home
            </Link>
            <Link
              to="/ai-tools"
              onClick={() => setMobileMenuOpen(false)}
              className={`block text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl px-4 py-3 transition-colors font-medium ${
                isActive('/ai-tools') ? 'text-blue-600 bg-blue-50' : ''
              }`}
            >
              AI Tools
            </Link>
            <Link
              to="/resources"
              onClick={() => setMobileMenuOpen(false)}
              className={`block text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl px-4 py-3 transition-colors font-medium ${
                isActive('/resources') ? 'text-blue-600 bg-blue-50' : ''
              }`}
            >
              Resources
            </Link>
            <Link
              to="/parent"
              onClick={() => setMobileMenuOpen(false)}
              className={`block text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl px-4 py-3 transition-colors font-medium ${
                location.pathname.startsWith('/parent') ? 'text-blue-600 bg-blue-50' : ''
              }`}
            >
              Parent Portal
            </Link>
            <Link
              to="/teacher-tips"
              onClick={() => setMobileMenuOpen(false)}
              className={`block text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl px-4 py-3 transition-colors font-medium ${
                isActive('/teacher-tips') ? 'text-blue-600 bg-blue-50' : ''
              }`}
            >
              Teacher Tips
            </Link>
            <Link
              to="/pricing"
              onClick={() => setMobileMenuOpen(false)}
              className={`block text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl px-4 py-3 transition-colors font-medium ${
                isActive('/pricing') ? 'text-blue-600 bg-blue-50' : ''
              }`}
            >
              Membership
            </Link>
            
            {/* Mobile Menu - User Actions */}
            {user ? (
              <div className="border-t border-slate-200 mt-4 pt-4 space-y-2">
                <Link
                  to="/account"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl px-4 py-3 transition-colors font-medium"
                >
                  My Account
                </Link>
                {user.is_admin && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-purple-600 hover:bg-purple-50 rounded-xl px-4 py-3 transition-colors font-medium"
                  >
                    Admin Dashboard
                  </Link>
                )}
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                  className="w-full text-left text-red-600 hover:bg-red-50 rounded-xl px-4 py-3 transition-colors font-medium"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <div className="border-t border-slate-200 mt-4 pt-4 space-y-2">
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-center text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl px-4 py-3 transition-colors font-medium"
                >
                  Sign In
                </Link>
                <Link
                  to="/pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-center bg-blue-600 text-white hover:bg-blue-700 rounded-xl px-4 py-3 transition-all shadow-sm font-medium"
                >
                  Try Free
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navigation;
